/** Fit weights are a simplex: they must total exactly 1.00 or the save is refused.
 *
 * So "set proximity to 25%" is not an edit to one number — the remaining 75 has to
 * come out of the others. This takes it proportionally to what they already hold,
 * which preserves their relative standing, and works in INTEGER CENTS so the total
 * is exactly 100 rather than a float that rounds to 0.99.
 *
 * Every component that is ON keeps at least 1%: a row reading "On — 0%" would be a
 * lie, and Off is how you say zero.
 */
export function rebalanceFitWeights(
  weights: Record<string, number>,
  key: string,
  pct: number,
  onKeys: string[],
): Record<string, number> {
  const others = onKeys.filter((k) => k !== key);
  const out: Record<string, number> = {};
  Object.keys(weights).forEach((k) => { out[k] = 0; });

  if (others.length === 0) {
    out[key] = 1;
    return out;
  }

  const target = Math.min(100 - others.length, Math.max(1, Math.round(pct)));
  // Seed every other component with its 1%, then share out what is genuinely left.
  const extra = 100 - target - others.length;
  const basis = others.map((k) => Math.round((weights[k] ?? 0) * 100));
  const total = basis.reduce((a, b) => a + b, 0);
  const exact = others.map((_, i) =>
    total > 0 ? (basis[i] / total) * extra : extra / others.length);

  const alloc = exact.map((x) => Math.floor(x));
  const leftover = extra - alloc.reduce((a, b) => a + b, 0);
  [...alloc.keys()]
    .sort((a, b) => (exact[b] - alloc[b]) - (exact[a] - alloc[a]) || a - b)
    .slice(0, leftover)
    .forEach((i) => { alloc[i] += 1; });

  out[key] = target / 100;
  others.forEach((k, i) => { out[k] = (alloc[i] + 1) / 100; });
  return out;
}
